import { marked } from "marked";
import { appConfig } from "./config.js";
import { Message, ContextData, ChatElements } from "./types.js";
import { ModelLoader } from "./model-loader.js";
import { ServiceWorkerManager } from "./service-worker-utils.js";
import { ExperimentBase } from "./experiment-base.js";
import { getScenariosAsContext } from "./ai-risk-scenarios.js";
import { LLMWrapper, ChatMessage } from "./llm-wrapper.js";

export class ChatApp extends ExperimentBase {
  public llm: LLMWrapper | null = null;
  private contextData: ContextData | null = null;
  private messages: Message[] = [];
  private isProcessing: boolean = false;
  private modelLoader: ModelLoader;
  private serviceWorkerManager: ServiceWorkerManager;
  private elements: ChatElements;
  private isInitializing: boolean = false;

  constructor() {
    super({
      id: "safety-qa",
      title: "Safety Q&A",
      tagline: "All the Trust and Safety Gemma 4 Can Deliver",
    });

    this.modelLoader = new ModelLoader();
    this.serviceWorkerManager = new ServiceWorkerManager();
  }

  protected setupContent(): void {
    this.elements = {
      messages: document.getElementById("messages")!,
      userInput: document.getElementById("user-input") as HTMLInputElement,
      sendBtn: document.getElementById("send-btn") as HTMLButtonElement,
      statusIndicator: document.querySelector(
        "#model-status .status-indicator",
      )!,
      statusText: document.querySelector("#model-status .status-text"),
      typingIndicator: document.getElementById("typingIndicator")!,
      suggestionsContainer: document.querySelector(".question-chips"),
      loadingOverlay: document.getElementById("loading-overlay"),
      loadingStatus: document.getElementById("loading-status"),
      errorOverlay: document.getElementById("error-overlay"),
    };

    // Start loading the model immediately, but only once
    if (!this.isInitializing && !this.llm) {
      this.isInitializing = true;
      document.body.classList.add("loading");
      this.init();
    }
  }

  public activate(): void {
    // Chat is always active once initialized
  }

  private async init(): Promise<void> {
    try {
      // Remove initial message to re-add with animation
      const initialMessage = document.querySelector(".initial-message");
      if (initialMessage) {
        initialMessage.remove();
      }

      // Render suggested questions
      this.renderSuggestedQuestions();

      // Register service worker for model caching
      await this.serviceWorkerManager.register((cached) => {
        this.modelLoader.setModelCached(cached);
        if (cached) {
          this.updateLoadingStatus("Loading AI from cache...", 90);
        }
      });

      await this.loadContextData();

      this.llm = await this.modelLoader.initializeModel(
        (status, text) => this.updateStatus(status, text),
        (text, progress) => this.updateLoadingStatus(text, progress),
      );

      this.setupEventListeners();
      this.hideLoadingOverlay();
      this.elements.userInput.disabled = false;
      this.elements.sendBtn.disabled = false;

      // Add welcome message after model loads
      this.addMessage("ai", appConfig.initialMessage);
    } catch (error) {
      this.updateStatus(
        "error",
        `Failed to initialize: ${(error as Error).message}`,
      );
      this.showErrorOverlay(error as Error);
      console.error("Initialization error:", error);
    }
  }

  public renderSuggestedQuestions(): void {
    if (!this.elements.suggestionsContainer) return;

    this.elements.suggestionsContainer.innerHTML = "";

    appConfig.suggestedQuestions.forEach((question) => {
      const button = document.createElement("button");
      button.className = "chip";
      button.textContent = question.text;
      button.onclick = () => this.askSuggestion(question.text);
      this.elements.suggestionsContainer!.appendChild(button);
    });
  }

  private async loadContextData(): Promise<void> {
    this.contextData = {
      systemPrompt: appConfig.systemPrompt,
      context: getScenariosAsContext(),
    };

    console.log("Full AI risk scenarios loaded as context");
  }

  private updateStatus(
    status: "loading" | "ready" | "error",
    text: string,
  ): void {
    this.elements.statusIndicator.dataset.status = status;
    if (this.elements.statusText) {
      this.elements.statusText.textContent = text;
    }

    // Show special error screen for misconfiguration
    if (status === "error" && text.includes("Misconfigured")) {
      const loadingContent = document.querySelector('.loading-content');
      if (loadingContent) {
        loadingContent.innerHTML = `
          <img src="logo.jpg" alt="MURD.AI" class="loading-logo" />
          <div class="loading-text">
            <div class="loading-title">Configuration Required</div>
            <div class="loading-subtitle" style="color: #ff6b6b; margin-top: 20px;">
              No .env file found
            </div>
            <div style="margin-top: 20px; font-size: 14px; line-height: 1.6; max-width: 400px; text-align: center;">
              MURD.AI requires a .env file with MODEL_BASE_URL configuration.
            </div>
            <div style="margin-top: 30px;">
              <a href="https://github.com/cory/murdai#configuration" 
                 style="display: inline-block; padding: 12px 24px; background: #0066cc; color: white; text-decoration: none; border-radius: 8px; font-weight: 500;">
                View Setup Instructions
              </a>
            </div>
          </div>
        `;
      }
    }
  }

  private updateLoadingStatus(text: string, progress?: number): void {
    if (this.elements.loadingStatus) {
      // Add cache indicator if model is cached
      const displayText =
        this.modelLoader.isModelCached() && text.includes("AI")
          ? `${text} (Cached)`
          : text;
      this.elements.loadingStatus.textContent = displayText;
    }

    // Update progress bar if progress is provided
    if (progress !== undefined) {
      const progressBar = document.getElementById(
        "loading-progress",
      ) as HTMLElement;
      if (progressBar) {
        progressBar.style.width = `${progress}%`;
      }
    }
  }

  private hideLoadingOverlay(): void {
    if (this.elements.loadingOverlay) {
      this.elements.loadingOverlay.classList.add("fade-out");
      document.body.classList.remove("loading");

      // Remove overlay from DOM after animation
      setTimeout(() => {
        if (this.elements.loadingOverlay) {
          this.elements.loadingOverlay.style.display = "none";
        }
      }, 500);
    }
  }

  private showErrorOverlay(error?: Error): void {
    if (this.elements.loadingOverlay) {
      this.elements.loadingOverlay.style.display = "none";
    }
    if (this.elements.errorOverlay) {
      this.elements.errorOverlay.style.display = "flex";
      if (error) {
        // Replace the static "Requires WebGPU" message with the actual error.
        const titleEl = this.elements.errorOverlay.querySelector(".error-title");
        if (titleEl) titleEl.textContent = "MURD.AI failed to start";
        const descs =
          this.elements.errorOverlay.querySelectorAll(".error-description");
        descs.forEach((el) => ((el as HTMLElement).style.display = "none"));
        const list = this.elements.errorOverlay.querySelector(".browser-list");
        if (list) (list as HTMLElement).style.display = "none";
        const detail = document.createElement("div");
        detail.style.cssText =
          "margin-top: 16px; max-width: 520px; font-family: monospace; font-size: 13px; line-height: 1.5; opacity: 0.9; word-break: break-word; text-align: left;";
        detail.textContent = `${error.name || "Error"}: ${error.message}`;
        const inner = this.elements.errorOverlay.querySelector(".error-content")
          ?? this.elements.errorOverlay;
        inner.appendChild(detail);
      }
    }
    document.body.classList.remove("loading");
  }

  private setupEventListeners(): void {
    this.elements.sendBtn.addEventListener("click", () => this.handleSend());

    this.elements.userInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.handleSend();
      }
    });
  }

  private async handleSend(): Promise<void> {
    const userMessage = this.elements.userInput.value.trim();
    if (!userMessage || this.isProcessing) return;

    this.isProcessing = true;
    this.elements.userInput.value = "";
    this.elements.userInput.disabled = true;
    this.elements.sendBtn.disabled = true;

    this.addMessage("user", userMessage);
    this.messages.push({ role: "user", content: userMessage });

    // Show typing indicator
    this.elements.typingIndicator.classList.add("active");

    // Scroll to typing indicator
    this.elements.typingIndicator.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });

    try {
      const messages = this.buildMessages();
      const assistantMessage = await this.llm!.chat(messages, 1024);

      this.elements.typingIndicator.classList.remove("active");

      this.addMessage("ai", assistantMessage);
      this.messages.push({ role: "assistant", content: assistantMessage });
    } catch (error) {
      this.elements.typingIndicator.classList.remove("active");
      this.addMessage(
        "ai",
        `Oops! 😅 I encountered an error: ${(error as Error).message}. Let me try again!`,
      );
      console.error("Generation error:", error);
    } finally {
      this.isProcessing = false;
      this.elements.userInput.disabled = false;
      this.elements.sendBtn.disabled = false;
      this.elements.userInput.focus();
    }
  }

  private buildMessages(): ChatMessage[] {
    const messages: ChatMessage[] = [];

    if (this.contextData?.systemPrompt) {
      let systemContent = this.contextData.systemPrompt;
      if (this.contextData.context) {
        systemContent +=
          "\n\nReference scenarios for AI safety questions:\n\n" +
          this.contextData.context;
      }
      systemContent += "\n\nUse markdown to make your points. Be conversational.";
      messages.push({ role: "system", content: systemContent });
    }

    // Keep the last 20 turns. Gemma 4 has plenty of context.
    const HISTORY_TURNS = 20;
    const recent = this.messages.slice(-HISTORY_TURNS);
    messages.push(...recent.map((m) => ({ role: m.role, content: m.content })));

    return messages;
  }

  private addMessage(type: "ai" | "user", content: string): void {
    const messageDiv = document.createElement("div");
    messageDiv.className = `message ${type}`;

    if (type === "ai") {
      const avatar = document.createElement("div");
      avatar.className = "avatar ai-avatar";
      avatar.innerHTML = '<img src="logo.jpg" alt="MURD">';
      messageDiv.appendChild(avatar);
    }

    const bubble = document.createElement("div");
    bubble.className = "message-bubble";

    const contentDiv = document.createElement("div");
    contentDiv.className = "message-content";

    // Render markdown for AI messages
    if (type === "ai") {
      marked.setOptions({
        breaks: true,
        gfm: true,
      });
      contentDiv.innerHTML = marked.parse(content) as string;
    } else {
      contentDiv.textContent = content;
    }

    bubble.appendChild(contentDiv);
    messageDiv.appendChild(bubble);

    this.elements.messages.appendChild(messageDiv);
    messageDiv.scrollIntoView({ behavior: "smooth", block: "end" });
  }

  public askSuggestion(question: string): void {
    // Clear the conversation history for a fresh start
    this.clearConversation();

    // Set the question and send it
    this.elements.userInput.value = question;
    this.handleSend();
  }

  private clearConversation(): void {
    this.messages = [];
    this.elements.messages.innerHTML = "";
    this.addMessage("ai", appConfig.initialMessage);
  }

  async clearModelCache(): Promise<void> {
    await this.serviceWorkerManager.clearCache();
  }
}
